import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [tabs] - Tab buttons (thumbnails)
 * @property {HTMLElement[]} [panels] - Tab panels, one comparison slider each
 * @property {HTMLElement[]} [filters] - Category filter buttons (optional)
 */

/**
 * Before / after gallery: one large comparison slider at a time, switched with an
 * accessible tab list (click, Arrow keys, Home / End) without reloading the page.
 * Optional category filters (toggle buttons) keep only the tabs of one category and show
 * the first of them; keyboard navigation then moves between the visible tabs only.
 *
 * @extends {Component<Refs>}
 */
class AbsolutBeforeAfter extends Component {
  /**
   * Shows the panel of the clicked tab.
   * @param {Event} event - Click event on a tab
   */
  select(event) {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    const tabs = this.refs.tabs ?? [];
    const index = tabs.findIndex((item) => item === tab);
    if (index !== -1) this.show(index);
  }

  /**
   * Keeps only the projects of the clicked category ("all" shows every project).
   * @param {Event} event - Click event on a filter button
   */
  filter(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-category]');
    if (!(button instanceof HTMLElement)) return;

    const category = button.dataset.category ?? 'all';
    for (const filter of this.refs.filters ?? []) {
      filter.setAttribute('aria-pressed', String(filter === button));
    }

    const tabs = this.refs.tabs ?? [];
    for (const tab of tabs) {
      tab.hidden = category !== 'all' && tab.dataset.category !== category;
    }

    const first = tabs.findIndex((tab) => !tab.hidden);
    if (first !== -1) this.show(first);
  }

  /**
   * Keyboard navigation inside the tab list.
   * @param {KeyboardEvent} event - Keydown event on a tab
   */
  navigate(event) {
    // Only the tabs left visible by the category filter
    const tabs = (this.refs.tabs ?? []).filter((tab) => !tab.hidden);
    if (!tabs.length || !(event.target instanceof Element)) return;

    const current = tabs.findIndex((item) => item === event.target.closest('[role="tab"]'));
    if (current === -1) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = rtl ? 'ArrowRight' : 'ArrowLeft';
    /** @type {number | undefined} */
    let next;

    if (event.key === forward) next = (current + 1) % tabs.length;
    else if (event.key === backward) next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === undefined) return;

    event.preventDefault();
    const target = tabs[next];
    if (!target) return;
    this.show((this.refs.tabs ?? []).indexOf(target));
    target.focus();
  }

  /**
   * Shows the panel at the given index.
   * @param {number} index - Panel index
   */
  show(index) {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];

    tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });
  }
}

if (!customElements.get('absolut-before-after')) {
  customElements.define('absolut-before-after', AbsolutBeforeAfter);
}
